import { AnimatePresence, motion } from "framer-motion";
import { EyeOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ROLES } from "../gameData";
import type { Influence } from "../types";

export type RoleCardVariant = "hand" | "exchange" | "loss";

interface Props {
  card: Influence;
  /** hand = fanned card at the bottom of the screen, exchange/loss = panel cards. */
  variant?: RoleCardVariant;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  /** Hold duration before the ability overlay appears. */
  holdDelay?: number;
  /** Lets a parent raise the slot's z-index while the ability is revealed. */
  onHoldChange?: (held: boolean) => void;
}

/** Pointer drift (px) that cancels a pending hold, so a scroll is never a hold. */
const HOLD_MOVE_TOLERANCE = 10;

export function RoleCard({
  card,
  variant = "hand",
  selectable = false,
  selected = false,
  onSelect,
  holdDelay = 450,
  onHoldChange
}: Props) {
  const role = card.role ? ROLES[card.role] : null;
  const [held, setHeld] = useState(false);
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  // Set when a hold fires, cleared SYNCHRONOUSLY by the click handler that
  // follows. Never reset on a timeout: that races React's click dispatch and
  // is exactly what makes holds eat ordinary taps.
  const didHold = useRef(false);
  const holdChange = useRef(onHoldChange);

  useEffect(() => {
    holdChange.current = onHoldChange;
  });

  useEffect(() => {
    holdChange.current?.(held);
  }, [held]);

  const clearHold = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => () => clearHold(), []);

  const endHold = () => {
    clearHold();
    origin.current = null;
    setHeld(false);
  };

  const revealable = Boolean(role);

  return (
    <motion.button
      type="button"
      className={[
        "role-card",
        `role-card--${variant}`,
        card.revealed ? "role-card--revealed" : "",
        selectable ? "role-card--selectable" : "",
        selected ? "role-card--selected" : "",
        held ? "role-card--held" : ""
      ]
        .filter(Boolean)
        .join(" ")}
      data-role={card.role ?? undefined}
      style={{ "--role-color": role?.color ?? "#ad8e5f" } as React.CSSProperties}
      // The lift is a framer transform, never a CSS one — the two fight and
      // framer's inline style always wins. In the hand the fan rotation lives
      // on the card (or its slot), so no framer transform is written there at
      // all, otherwise it would flatten the fan.
      animate={variant === "hand" ? undefined : { y: selected ? -6 : 0 }}
      transition={{ type: "spring", stiffness: 520, damping: 30 }}
      whileTap={selectable ? { scale: 0.96 } : undefined}
      onPointerDown={(event) => {
        if (event.button > 0 || !revealable) return;
        clearHold();
        didHold.current = false;
        origin.current = { x: event.clientX, y: event.clientY };
        timer.current = window.setTimeout(() => {
          timer.current = null;
          didHold.current = true;
          setHeld(true);
          navigator.vibrate?.(12);
        }, holdDelay);
      }}
      onPointerMove={(event) => {
        if (timer.current === null || !origin.current) return;
        const drift = Math.hypot(
          event.clientX - origin.current.x,
          event.clientY - origin.current.y
        );
        if (drift > HOLD_MOVE_TOLERANCE) clearHold();
      }}
      onPointerUp={endHold}
      onPointerCancel={endHold}
      onPointerLeave={endHold}
      onContextMenu={(event) => event.preventDefault()}
      onClick={() => {
        if (didHold.current) {
          didHold.current = false;
          return;
        }
        if (selectable) onSelect?.();
      }}
      aria-pressed={selectable ? selected : undefined}
      aria-label={
        selectable
          ? `${selected ? "Deselect" : "Select"} ${role?.name ?? "card"}`
          : card.revealed
            ? `Lost ${role?.name ?? "Influence"}`
            : `${role?.name ?? "Hidden"} Influence`
      }
    >
      {role ? (
        <img className="role-card__art" src={role.image} alt="" draggable={false} />
      ) : (
        <span className="role-card__art role-card__art--hidden" aria-hidden="true">
          ?
        </span>
      )}
      <span className="role-card__plate">{role?.name ?? "Hidden"}</span>

      {selected && <span className="role-card__check">KEEP</span>}

      <AnimatePresence>
        {held && role && (
          <motion.span
            className="role-card__reveal"
            key="reveal"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
          >
            {/* The name plate stays visible under this overlay, so the reveal
                carries the rules only — no duplicated role name. */}
            <span>{role.ability}</span>
            <em>{role.blocks}</em>
          </motion.span>
        )}
      </AnimatePresence>

      {card.revealed && (
        <span className="role-card__lost">
          <EyeOff size={14} /> LOST
        </span>
      )}
    </motion.button>
  );
}
